// axios 实例 + 通用拦截器
//
// 注入头来源参考 Android: HttpCommonInterceptor.java
//   Authorization  ← token（原值，无 Bearer 前缀）
//   App-Id         ← 后端白名单的 web 端 id（通过 env 配置）
//   App-Channel    ← 渠道，web 一般固定
//   Device-Id      ← localStorage 持久 UUID
//   Device-TYPE    ← "web"
//   Version-Code   ← 构建时注入
//   Country        ← navigator/i18n
//   Language       ← navigator.language 截断
//   Content-Type   ← application/json;charset=utf-8（axios 默认）

import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { showToast } from "vant";
import { getDeviceId } from "@/utils/device";
import { edition } from "@edition";
import { i18n } from "@/i18n";
import { ApiEnvelope, ApiError } from "./types";

// 给请求 config 加 silent 开关：置 true 时本次请求的业务/网络错误不弹全局 toast，
// 交由调用方自行处理（如支付轮询、下单失败走自定义弹窗）。
declare module "axios" {
  interface AxiosRequestConfig {
    silent?: boolean;
  }
}

// 版本相关接入参数统一从 edition 配置读取（src/edition/<cn|global>/index.ts），
// 构建期由 @edition 别名按 VITE_EDITION 选中其一；业务代码不再散读 import.meta.env。
//   baseUrl     dev 留空走相对路径（vite proxy 转发）；prod 注入绝对地址或同源反代留空
//   appId       后端白名单的客户端包名（默认值见各版本 manifest，可用 .env.* 覆盖）
const BASE_URL = edition.api.baseUrl;
const APP_ID = edition.api.appId;
const APP_CHANNEL = edition.api.channel;
const VERSION_CODE = edition.api.versionCode;

// 这些业务码不自动弹 toast（由调用方决定怎么提示）。
// 存档状态预检：201017/201019/201020=逻辑正确(有/无存档等)，201018=存档上传中。
const SILENT_CODES = new Set([201017, 201018, 201019, 201020]);

const TOKEN_KEY = "jzbro_token";
const IS_GUEST_KEY = "jzbro_is_guest";
const GUEST_USERNAME_KEY = "jzbro_guest_username";
const GUEST_PASSWORD_KEY = "jzbro_guest_password";

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? "";
}
export function setToken(token: string): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  // 触发自定义事件让 Pinia user store 同步 ref（解决 interceptor 清 token
  // 后 isLoggedIn 仍 true 但实际请求无认证的鬼鬼祟祟问题）
  try {
    window.dispatchEvent(
      new CustomEvent("jzbro:token-changed", { detail: { token } }),
    );
  } catch {
    /* SSR 等非浏览器环境 */
  }
}

// ============ 游客身份持久化 ============
// 参照 Gamino TokenStorage 设计：token 与 guest creds 解耦
//   logout() / token 过期 → 只清 token，guest creds 保留 → 下次能自动重登
//   resetGuestIdentity() → 清 guest creds + 换 Device-Id，下次会创建新账号

export function getIsGuest(): boolean {
  return localStorage.getItem(IS_GUEST_KEY) === "1";
}
export function setIsGuest(flag: boolean): void {
  if (flag) localStorage.setItem(IS_GUEST_KEY, "1");
  else localStorage.removeItem(IS_GUEST_KEY);
}

export interface GuestCredentials {
  username: string;
  password: string;
}
export function getGuestCredentials(): GuestCredentials | null {
  const username = localStorage.getItem(GUEST_USERNAME_KEY) ?? "";
  const password = localStorage.getItem(GUEST_PASSWORD_KEY) ?? "";
  return username && password ? { username, password } : null;
}
export function setGuestCredentials(creds: GuestCredentials): void {
  localStorage.setItem(GUEST_USERNAME_KEY, creds.username);
  localStorage.setItem(GUEST_PASSWORD_KEY, creds.password);
}
export function clearGuestCredentials(): void {
  localStorage.removeItem(GUEST_USERNAME_KEY);
  localStorage.removeItem(GUEST_PASSWORD_KEY);
  localStorage.removeItem(IS_GUEST_KEY);
}

function detectLanguage(): string {
  // 跟随用户所选语言：i18n 初始化时已把「已存选择 / 首次浏览器探测结果」落盘到
  // jzbro_locale，这里读它作为 Language 头。后端靠这个头同步内容（无专门设语言接口，
  // 对齐 Android：切语言只改本地 + 后续请求带新 Language 头）。取不到时回退本版默认语言。
  try {
    const saved = localStorage.getItem("jzbro_locale");
    if (saved) return saved;
  } catch {
    /* 隐私模式 / 非浏览器环境 */
  }
  return edition.ui.defaultLocale;
}

function detectCountry(): string {
  // 浏览器没有可靠的 country API，用 timezone 粗略推断；后端如果需要精确国别，
  // 应该改用 IP 反查或要求登录后明示。
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Asia/Shanghai") || tz.startsWith("Asia/Chongqing"))
      return "CN";
    if (tz.startsWith("Asia/Hong_Kong")) return "HK";
    if (tz.startsWith("Asia/Taipei")) return "TW";
    if (tz.startsWith("Asia/Tokyo")) return "JP";
    if (tz.startsWith("Asia/Bangkok")) return "TH";
    if (tz.startsWith("America/")) return "US";
  } catch {
    /* ignore */
  }
  return "CN";
}

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  // 分页列表 + 详情接口偶尔响应较大（pics 数组动辄几十 KB），给宽裕一点
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
});

http.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const headers = cfg.headers;
  const token = getToken();
  if (token) headers.set("Authorization", token);
  headers.set("App-Id", APP_ID);
  headers.set("App-Channel", APP_CHANNEL);
  headers.set("Device-Id", getDeviceId());
  // TODO: 后端在客户端表里给 (com.cga.cloudgame.platform, web) 注册一行后改回 "web"
  //   目前 Device-TYPE=web 会被网关拒：code 610085 "client does not exist"
  //   暂时伪装成 android 以便联调走通，但服务端日志会记成 Android 客户端
  headers.set("Device-TYPE", "android");
  headers.set("Version-Code", VERSION_CODE);
  headers.set("Country", detectCountry());
  headers.set("Language", detectLanguage());
  return cfg;
});

http.interceptors.response.use(
  (resp) => {
    const body = resp.data as ApiEnvelope<unknown>;
    // 后端可能直接返非 envelope（比如静态资源），保险起见判断一下
    if (typeof body !== "object" || body === null || !("code" in body)) {
      return resp;
    }
    if (body.code !== 0) {
      const detail = body.detail || body.msg || i18n.global.t("net.requestFailed");
      // 401 等需要登录的错误码（具体值需要确认后端定义）
      if (body.code === 401 || body.code === 10001) {
        setToken("");
        // 让 401 不自动弹 toast，由调用方决定（通常路由跳登录页）
      } else if (!SILENT_CODES.has(body.code) && !resp.config.silent) {
        showToast({ message: detail, position: "top" });
      }
      throw new ApiError(body.code, detail, body);
    }
    return resp;
  },
  (err: AxiosError) => {
    // 后端经常用 HTTP 5xx 返业务错误（比如排队 303023 也是 500），
    // 如果 response body 是我们的 envelope，按 ApiError 处理，把 detail 展示给用户
    const body = err.response?.data as ApiEnvelope<unknown> | undefined;
    if (
      body &&
      typeof body === "object" &&
      "code" in body &&
      typeof body.code === "number" &&
      body.code !== 0
    ) {
      const detail =
        body.detail || body.msg || i18n.global.t("net.errorWithCode", { code: body.code });
      if (body.code !== 401 && body.code !== 10001) {
        if (!err.config?.silent) showToast({ message: detail, position: "top" });
      } else {
        setToken("");
      }
      return Promise.reject(new ApiError(body.code, detail, body));
    }
    // 真·网络层错误（断网、超时、CORS、没有 envelope 的 500）
    const msg = err.response
      ? i18n.global.t("net.serverError", { status: err.response.status })
      : err.code === "ECONNABORTED"
        ? i18n.global.t("net.timeout")
        : i18n.global.t("net.networkError");
    if (!err.config?.silent) showToast({ message: msg, position: "top" });
    return Promise.reject(err);
  },
);

/** 取出 envelope 里的 data，业务代码直接用这个。 */
export async function apiGet<T>(
  url: string,
  params?: Record<string, unknown>,
  config?: { silent?: boolean },
): Promise<T> {
  const resp = await http.get<ApiEnvelope<T>>(url, { params, ...config });
  return resp.data.data;
}

export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: { silent?: boolean },
): Promise<T> {
  const resp = await http.post<ApiEnvelope<T>>(url, body, config);
  return resp.data.data;
}

/** 后端有几个 DELETE 是带 body 的（取消订阅 / 取消收藏），axios 需要 data 字段 */
export async function apiDeleteWithBody<T>(
  url: string,
  body?: unknown,
): Promise<T> {
  const resp = await http.delete<ApiEnvelope<T>>(url, { data: body });
  return resp.data.data;
}
